import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Chip,
  Badge,
  Fade,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupIcon from "@mui/icons-material/Group";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ClearIcon from "@mui/icons-material/Clear";
import * as XLSX from "xlsx";

const DaywiseReport = () => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRm, setExpandedRm] = useState({});
  const [expandedMr, setExpandedMr] = useState({});
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dateColumns, setDateColumns] = useState([]);
  const [error, setError] = useState(null);

  // Filter states
  const [searchRm, setSearchRm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const [uniqueProducts, setUniqueProducts] = useState([]);

  // Fetch daywise report data
  const fetchDaywiseReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5004";
      const url = `${baseUrl}/daywise-report`;

      console.log("Fetching from:", url);
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ month, year }),
      });

      console.log("Response status:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("API Response:", result);

      if (result.success && result.data) {
        setData(result.data);
        setDateColumns(result.allDates || []);

        // Extract unique products
        const products = [...new Set(result.data.map((item) => item.product))]
          .filter(Boolean)
          .sort();
        setUniqueProducts(products);
      } else {
        setError("No data received from API");
      }
    } catch (err) {
      console.error("Error fetching daywise report:", err);
      setError(`Failed to fetch data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Transform data into hierarchical structure with automatic summation
  const [hierarchicalData, setHierarchicalData] = useState([]);

  useEffect(() => {
    if (data && data.length > 0) {
      const hierarchy = {};

      data.forEach((row) => {
        // Initialize RM level
        if (!hierarchy[row.RmMgName]) {
          hierarchy[row.RmMgName] = {
            RmMgName: row.RmMgName,
            ...dateColumns.reduce((acc, date) => ({ ...acc, [date]: 0 }), {}),
            children: {},
          };
        }

        const rm = hierarchy[row.RmMgName];

        // Initialize MR level
        if (!rm.children[row.MrName]) {
          rm.children[row.MrName] = {
            MrName: row.MrName,
            ...dateColumns.reduce((acc, date) => ({ ...acc, [date]: 0 }), {}),
            children: {},
          };
        }

        const mr = rm.children[row.MrName];

        // Initialize Doctor level
        if (!mr.children[row.DoctName]) {
          mr.children[row.DoctName] = {
            DoctName: row.DoctName,
            MclCode: row.MclCode,
            product: row.product,
            ...dateColumns.reduce(
              (acc, date) => ({ ...acc, [date]: row[date] || 0 }),
              {}
            ),
          };
        }

        const doctor = mr.children[row.DoctName];

        // Sum values up the hierarchy
        dateColumns.forEach((date) => {
          const value = row[date] || 0;
          doctor[date] = value;
          mr[date] += value;
          rm[date] += value;
        });
      });

      // Transform to array format
      const transformed = Object.values(hierarchy).map((rm) => ({
        RmMgName: rm.RmMgName,
        ...dateColumns.reduce(
          (acc, date) => ({ ...acc, [date]: rm[date] }),
          {}
        ),
        children: Object.values(rm.children).map((mr) => ({
          MrName: mr.MrName,
          ...dateColumns.reduce(
            (acc, date) => ({ ...acc, [date]: mr[date] }),
            {}
          ),
          children: Object.values(mr.children),
        })),
      }));

      setHierarchicalData(transformed);
    }
  }, [data, dateColumns]);

  // Filter hierarchical data based on search criteria
  const groupedData = hierarchicalData
    .filter(
      (rm) =>
        searchRm === "" ||
        rm.RmMgName?.toLowerCase().includes(searchRm.toLowerCase())
    )
    .reduce((acc, rm) => {
      acc[rm.RmMgName] = {};
      rm.children.forEach((mr) => {
        const filteredDoctors = mr.children.filter(
          (doc) => selectedProduct === "" || doc.product === selectedProduct
        );
        if (filteredDoctors.length > 0) {
          acc[rm.RmMgName][mr.MrName] = {
            ...mr,
            children: filteredDoctors,
            // Recalculate MR totals for filtered doctors
            ...dateColumns.reduce(
              (accDate, date) => ({
                ...accDate,
                [date]: filteredDoctors.reduce(
                  (sum, doc) => sum + (doc[date] || 0),
                  0
                ),
              }),
              {}
            ),
          };
        }
      });
      return acc;
    }, {});

  // Toggle RM expansion
  const toggleRmExpansion = (rmName) => {
    setExpandedRm((prev) => ({
      ...prev,
      [rmName]: !prev[rmName],
    }));
  };

  // Toggle MR expansion
  const toggleMrExpansion = (rmName, mrName) => {
    const key = `${rmName}|||${mrName}`;
    setExpandedMr((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Filter doctor data by selected day
  const filterDoctorByDay = (doctor) => {
    if (selectedDay === "") return true;
    const dateKey = `${String(selectedDay).padStart(2, "0")}-${String(
      month
    ).padStart(2, "0")}-${year}`;
    return doctor[dateKey] > 0;
  };

  // Export to Excel
  const handleExport = () => {
    const exportData = [];
    Object.entries(groupedData).forEach(([rmName, mrData]) => {
      Object.entries(mrData).forEach(([mrName, doctors]) => {
        doctors.forEach((doctor) => {
          // Filter by day if selected
          if (filterDoctorByDay(doctor)) {
            const row = {
              "RM Name": rmName,
              "MR Name": mrName,
              "Doctor Name": doctor.DoctName,
              "MCL Code": doctor.MclCode,
              Product: doctor.product,
            };
            dateColumns.forEach((date) => {
              row[date] = doctor[date] || 0;
            });
            exportData.push(row);
          }
        });
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Daywise Report");
    XLSX.writeFile(workbook, `daywise-report-${month}-${year}.xlsx`);
  };

  // Calculate RM total (sum of all dates for that RM)
  const calculateRmTotal = (rmName) => {
    let total = 0;
    Object.values(groupedData[rmName] || {}).forEach((mr) => {
      dateColumns.forEach((date) => {
        total += mr[date] || 0;
      });
    });
    return total;
  };

  // Calculate MR total (sum of all dates for that MR)
  const calculateMrTotal = (mr) => {
    let total = 0;
    dateColumns.forEach((date) => {
      total += mr[date] || 0;
    });
    return total;
  };

  // Get RM date total (sum from all MRs for that date)
  const calculateRmDateTotal = (rmName, date) => {
    let total = 0;
    Object.values(groupedData[rmName] || {}).forEach((mr) => {
      total += mr[date] || 0;
    });
    return total;
  };

  // Get MR date total (already stored in MR object)
  const calculateMrDateTotal = (mr, date) => {
    return mr[date] || 0;
  };

  // Calculate statistics
  const stats = {
    totalMr: Object.values(groupedData).reduce(
      (sum, mrData) => sum + Object.keys(mrData).length,
      0
    ),
  };

  useEffect(() => {
    fetchDaywiseReport();
  }, [month, year]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ backgroundColor: "#0f172a" }}
      >
        <CircularProgress sx={{ color: "#3b82f6" }} size={60} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#0f172a",
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #0f172a 0%, #1a2332 50%, #0f172a 100%)",
      }}
    >
      {/* Header Card with Glassmorphism */}
      <Card
        sx={{
          mb: 4,
          background:
            "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)",
          backdropFilter: "blur(20px)",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(59, 130, 246, 0.2)",
          border: "1px solid rgba(59, 130, 246, 0.2)",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 12px 40px rgba(59, 130, 246, 0.3)",
            border: "1px solid rgba(59, 130, 246, 0.4)",
          },
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 3,
            }}
          >
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <AssignmentIcon sx={{ fontSize: "2.5rem", color: "#3b82f6" }} />
                <Typography
                  variant="h4"
                  sx={{
                    background:
                      "linear-gradient(45deg, #3b82f6 0%, #a855f7 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontWeight: "bold",
                  }}
                >
                  Prescription Daywise Report
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)", ml: 5 }}
              >
                📍 Doctor-wise visit tracking with real-time analytics
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Tooltip title="Month (1-12)">
                <TextField
                  type="number"
                  label="Month"
                  value={month}
                  onChange={(e) => setMonth(parseInt(e.target.value))}
                  inputProps={{ min: 1, max: 12 }}
                  sx={{
                    width: 100,
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.6)",
                      },
                      "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                      transition: "all 0.3s ease",
                    },
                    "& .MuiInputBase-input": { color: "white" },
                    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
                  }}
                />
              </Tooltip>
              <Tooltip title="Year">
                <TextField
                  type="number"
                  label="Year"
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value))}
                  sx={{
                    width: 100,
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.6)",
                      },
                      "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                      transition: "all 0.3s ease",
                    },
                    "& .MuiInputBase-input": { color: "white" },
                    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
                  }}
                />
              </Tooltip>
              <Tooltip title="Refresh data">
                <Button
                  variant="contained"
                  sx={{
                    background:
                      "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)",
                    color: "white",
                    fontWeight: "bold",
                    borderRadius: "10px",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(59, 130, 246, 0.4)",
                    },
                    transition: "all 0.3s ease",
                  }}
                  startIcon={<RefreshIcon />}
                  onClick={fetchDaywiseReport}
                >
                  Refresh
                </Button>
              </Tooltip>
              <Tooltip title="Export to Excel">
                <Button
                  variant="contained"
                  sx={{
                    background:
                      "linear-gradient(45deg, #10b981 0%, #059669 100%)",
                    color: "white",
                    fontWeight: "bold",
                    borderRadius: "10px",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(16, 185, 129, 0.4)",
                    },
                    transition: "all 0.3s ease",
                  }}
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                >
                  Export
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Card
          sx={{
            mb: 4,
            backgroundColor: "#7f1d1d",
            borderLeft: "4px solid #ef4444",
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Typography sx={{ color: "#fca5a5" }}>❌ {error}</Typography>
          </CardContent>
        </Card>
      )}

      {/* Filter Section with Modern Design */}
      <Card
        sx={{
          mb: 4,
          backgroundColor: "rgba(30, 41, 59, 0.8)",
          backdropFilter: "blur(10px)",
          borderRadius: 2,
          borderTop: "2px solid #3b82f6",
          border: "1px solid rgba(59, 130, 246, 0.2)",
          transition: "all 0.3s ease",
          "&:hover": {
            backgroundColor: "rgba(30, 41, 59, 0.95)",
            boxShadow: "0 4px 20px rgba(59, 130, 246, 0.15)",
          },
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <TextField
              placeholder="Search RM Name"
              value={searchRm}
              onChange={(e) => setSearchRm(e.target.value)}
              sx={{
                flex: 1,
                minWidth: 200,
                "& .MuiOutlinedInput-root": {
                  color: "white",
                  backgroundColor: "rgba(15, 23, 42, 0.6)",
                  "& fieldset": {
                    borderColor: "rgba(59, 130, 246, 0.4)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(59, 130, 246, 0.7)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3b82f6",
                    boxShadow: "0 0 10px rgba(59, 130, 246, 0.3)",
                  },
                  transition: "all 0.3s ease",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "rgba(255,255,255,0.5)",
                  opacity: 1,
                },
                "& .MuiInputBase-input": { color: "white" },
                "& .MuiInputLabel-root": { color: "white" },
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "#3b82f6" }} />,
              }}
            />

            <FormControl
              sx={{
                minWidth: 180,
                "& .MuiOutlinedInput-root": {
                  color: "white",
                  backgroundColor: "rgba(15, 23, 42, 0.6)",
                  "& fieldset": {
                    borderColor: "rgba(59, 130, 246, 0.4)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(59, 130, 246, 0.7)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3b82f6",
                    boxShadow: "0 0 10px rgba(59, 130, 246, 0.3)",
                  },
                  transition: "all 0.3s ease",
                },
                "& .MuiInputBase-input": { color: "white" },
                "& .MuiSvgIcon-root": { color: "#3b82f6" },
                "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
              }}
            >
              <InputLabel>Product</InputLabel>
              <Select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                label="Product"
              >
                <MenuItem value="">All Products</MenuItem>
                {uniqueProducts.map((product) => (
                  <MenuItem key={product} value={product}>
                    {product}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              sx={{
                minWidth: 150,
                "& .MuiOutlinedInput-root": {
                  color: "white",
                  backgroundColor: "rgba(15, 23, 42, 0.6)",
                  "& fieldset": {
                    borderColor: "rgba(59, 130, 246, 0.4)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(59, 130, 246, 0.7)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3b82f6",
                    boxShadow: "0 0 10px rgba(59, 130, 246, 0.3)",
                  },
                  transition: "all 0.3s ease",
                },
                "& .MuiInputBase-input": { color: "white" },
                "& .MuiSvgIcon-root": { color: "#3b82f6" },
                "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
              }}
            >
              <InputLabel>Day</InputLabel>
              <Select
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value)}
                label="Day"
              >
                <MenuItem value="">All Days</MenuItem>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <MenuItem key={day} value={day.toString()}>
                    Day {day}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip title="Clear all filters">
              <Button
                variant="outlined"
                sx={{
                  borderColor: "#3b82f6",
                  color: "#3b82f6",
                  fontWeight: "bold",
                  borderRadius: "8px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#2563eb",
                    backgroundColor: "rgba(59, 130, 246, 0.15)",
                    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
                    transform: "translateY(-2px)",
                  },
                }}
                startIcon={<ClearIcon />}
                onClick={() => {
                  setSearchRm("");
                  setSelectedProduct("");
                  setSelectedDay("");
                }}
              >
                Clear
              </Button>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* Table Container */}
      <Card
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(59, 130, 246, 0.2)",
          border: "1px solid rgba(59, 130, 246, 0.2)",
          backgroundColor: "#1e293b",
          mb: 4,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 12px 40px rgba(59, 130, 246, 0.25)",
          },
        }}
      >
        <TableContainer
          sx={{
            maxHeight: "calc(100vh - 400px)",
            backgroundColor: "#1e293b",
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow
                sx={{
                  background:
                    "linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)",
                }}
              >
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    width: 40,
                    fontSize: "0.85rem",
                    padding: "8px 4px",
                  }}
                >
                  #
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    minWidth: 150,
                    fontSize: "0.85rem",
                    padding: "8px 6px",
                  }}
                >
                  RM / MR / Doctor
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    width: 70,
                    fontSize: "0.8rem",
                    padding: "8px 4px",
                    textAlign: "center",
                  }}
                >
                  MCL
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    width: 80,
                    fontSize: "0.8rem",
                    padding: "8px 4px",
                    textAlign: "center",
                  }}
                >
                  Product
                </TableCell>

                {/* Date Columns */}
                {dateColumns.map((date) => (
                  <TableCell
                    key={date}
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      textAlign: "center",
                      width: 38,
                      minWidth: 38,
                      backgroundColor: "#2563eb",
                      fontSize: "0.75rem",
                      padding: "6px 2px",
                    }}
                  >
                    {date.split("-")[0]}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {Object.entries(groupedData).length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4 + dateColumns.length}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <Typography variant="h6" sx={{ color: "#94a3b8" }}>
                      No data available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                Object.entries(groupedData).map(([rmName, mrData], rmIndex) => (
                  <React.Fragment key={rmName}>
                    {/* RM Row */}
                    <TableRow
                      sx={{
                        backgroundColor: "rgba(59, 130, 246, 0.08)",
                        fontWeight: "bold",
                        "&:hover": {
                          backgroundColor: "rgba(59, 130, 246, 0.15)",
                          boxShadow: "inset 0 0 20px rgba(59, 130, 246, 0.1)",
                        },
                        cursor: "pointer",
                        borderLeft: "4px solid #3b82f6",
                        transition: "all 0.3s ease",
                      }}
                      onClick={() => toggleRmExpansion(rmName)}
                    >
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          color: "#3b82f6",
                          fontSize: "0.8rem",
                          padding: "4px 2px",
                          width: 30,
                        }}
                      >
                        {rmIndex + 1}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          color: "#3b82f6",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontSize: "0.8rem",
                          padding: "4px 6px",
                        }}
                      >
                        {expandedRm[rmName] ? (
                          <KeyboardArrowUpIcon sx={{ fontSize: "1.2rem" }} />
                        ) : (
                          <KeyboardArrowDownIcon sx={{ fontSize: "1.2rem" }} />
                        )}
                        📍 {rmName}
                      </TableCell>
                      <TableCell colSpan={2} sx={{ padding: "4px 2px" }} />

                      {/* RM Date Values */}
                      {dateColumns.map((date) => {
                        const value = calculateRmDateTotal(rmName, date);
                        return (
                          <TableCell
                            key={date}
                            sx={{
                              textAlign: "center",
                              fontWeight: "bold",
                              color: value > 0 ? "#10b981" : "#3b82f6",
                              backgroundColor:
                                value > 0
                                  ? "rgba(16, 185, 129, 0.2)"
                                  : "rgba(59, 130, 246, 0.15)",
                              fontSize: "0.75rem",
                              padding: "4px 2px",
                              width: 38,
                            }}
                          >
                            {value}
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    {/* MR and Doctor Rows */}
                    {expandedRm[rmName] &&
                      Object.entries(mrData).map(([mrName, mr], mrIndex) => (
                        <React.Fragment key={`${rmName}|||${mrName}`}>
                          {/* MR Row */}
                          <TableRow
                            sx={{
                              backgroundColor: "rgba(168, 85, 247, 0.06)",
                              borderLeft: "4px solid #a855f7",
                              "&:hover": {
                                backgroundColor: "rgba(168, 85, 247, 0.12)",
                                boxShadow:
                                  "inset 0 0 20px rgba(168, 85, 247, 0.1)",
                              },
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                            }}
                            onClick={() => toggleMrExpansion(rmName, mrName)}
                          >
                            <TableCell
                              sx={{
                                pl: 2,
                                fontWeight: "bold",
                                color: "#a855f7",
                                fontSize: "0.8rem",
                                padding: "4px 2px",
                                width: 30,
                              }}
                            >
                              {rmIndex + 1}.{mrIndex + 1}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                color: "#a855f7",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                fontSize: "0.8rem",
                                padding: "4px 6px",
                              }}
                            >
                              {expandedMr[`${rmName}|||${mrName}`] ? (
                                <KeyboardArrowUpIcon
                                  sx={{ fontSize: "1.2rem" }}
                                />
                              ) : (
                                <KeyboardArrowDownIcon
                                  sx={{ fontSize: "1.2rem" }}
                                />
                              )}
                              👤 {mrName}
                            </TableCell>
                            <TableCell
                              colSpan={2}
                              sx={{ padding: "4px 2px" }}
                            />

                            {/* MR Date Values */}
                            {dateColumns.map((date) => {
                              const value = calculateMrDateTotal(mr, date);
                              return (
                                <TableCell
                                  key={date}
                                  sx={{
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    color: value > 0 ? "#10b981" : "#a855f7",
                                    backgroundColor:
                                      value > 0
                                        ? "rgba(16, 185, 129, 0.2)"
                                        : "rgba(168, 85, 247, 0.15)",
                                    fontSize: "0.75rem",
                                    padding: "4px 2px",
                                    width: 38,
                                  }}
                                >
                                  {value}
                                </TableCell>
                              );
                            })}
                          </TableRow>

                          {/* Doctor Rows */}
                          {expandedMr[`${rmName}|||${mrName}`] &&
                            mr.children
                              .filter(filterDoctorByDay)
                              .map((doctor, docIndex) => (
                                <TableRow
                                  key={`${rmName}|||${mrName}|||${doctor.DoctName}|||${docIndex}`}
                                  sx={{
                                    backgroundColor:
                                      docIndex % 2 === 0
                                        ? "rgba(16, 185, 129, 0.05)"
                                        : "transparent",
                                    borderLeft: "4px solid #10b981",
                                    "&:hover": {
                                      backgroundColor:
                                        "rgba(16, 185, 129, 0.15)",
                                      boxShadow:
                                        "inset 0 0 20px rgba(16, 185, 129, 0.1)",
                                      transform: "scale(1.01)",
                                    },
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      pl: 1,
                                      fontWeight: "bold",
                                      color: "#10b981",
                                      fontSize: "0.75rem",
                                      padding: "4px 2px",
                                      width: 30,
                                    }}
                                  >
                                    {rmIndex + 1}.{mrIndex + 1}.{docIndex + 1}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: "#10b981",
                                      pl: 0.5,
                                      fontSize: "0.75rem",
                                      padding: "4px 6px",
                                    }}
                                  >
                                    💊 {doctor.DoctName}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: "#10b981",
                                      textAlign: "center",
                                      fontSize: "0.75rem",
                                      padding: "4px 2px",
                                    }}
                                  >
                                    {doctor.MclCode}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: "#10b981",
                                      textAlign: "center",
                                      fontSize: "0.75rem",
                                      padding: "4px 2px",
                                    }}
                                  >
                                    {doctor.product}
                                  </TableCell>

                                  {/* Date Visit Scores */}
                                  {dateColumns.map((date) => (
                                    <TableCell
                                      key={date}
                                      sx={{
                                        textAlign: "center",
                                        fontWeight: "bold",
                                        color:
                                          doctor[date] > 0
                                            ? "#10b981"
                                            : "#64748b",
                                        backgroundColor:
                                          doctor[date] > 0
                                            ? "rgba(16, 185, 129, 0.2)"
                                            : "rgba(100, 116, 139, 0.05)",
                                        fontSize: "0.9rem",
                                        padding: "4px 2px",
                                        width: 38,
                                        transition: "all 0.3s ease",
                                        borderRadius: "4px",
                                        "&:hover": {
                                          backgroundColor:
                                            doctor[date] > 0
                                              ? "rgba(16, 185, 129, 0.3)"
                                              : "rgba(100, 116, 139, 0.1)",
                                          transform: "scale(1.1)",
                                        },
                                      }}
                                    >
                                      {doctor[date] ? (
                                        <Chip
                                          label={doctor[date]}
                                          size="small"
                                          sx={{
                                            backgroundColor:
                                              "rgba(16, 185, 129, 0.3)",
                                            color: "#10b981",
                                            fontWeight: "bold",
                                          }}
                                        />
                                      ) : (
                                        "-"
                                      )}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}

                          {/* MR Total Row */}
                          {expandedMr[`${rmName}|||${mrName}`] && (
                            <TableRow
                              sx={{
                                backgroundColor: "rgba(168, 85, 247, 0.25)",
                                fontWeight: "bold",
                                borderLeft: "4px solid #a855f7",
                              }}
                            >
                              <TableCell
                                sx={{
                                  pl: 4,
                                  fontWeight: "bold",
                                  color: "#a855f7",
                                  fontSize: "0.95rem",
                                }}
                              >
                                {rmIndex + 1}.{mrIndex + 1}
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  color: "#a855f7",
                                  fontSize: "0.95rem",
                                }}
                              >
                                📊 MR Total
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  color: "#a855f7",
                                  textAlign: "center",
                                  fontSize: "0.95rem",
                                }}
                              />
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  color: "#a855f7",
                                  textAlign: "center",
                                  fontSize: "0.95rem",
                                }}
                              >
                                {calculateMrTotal(mr)}
                              </TableCell>

                              {/* MR Date Totals */}
                              {dateColumns.map((date) => (
                                <TableCell
                                  key={date}
                                  sx={{
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    color: "#a855f7",
                                    backgroundColor: "rgba(168, 85, 247, 0.15)",
                                    fontSize: "0.95rem",
                                  }}
                                >
                                  {calculateMrDateTotal(mr, date)}
                                </TableCell>
                              ))}
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}

                    {/* RM Total Row */}
                    {expandedRm[rmName] && (
                      <TableRow
                        sx={{
                          backgroundColor: "rgba(59, 130, 246, 0.25)",
                          fontWeight: "bold",
                          borderLeft: "4px solid #3b82f6",
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            color: "#3b82f6",
                            fontSize: "0.95rem",
                          }}
                        >
                          {rmIndex + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            color: "#3b82f6",
                            fontSize: "0.95rem",
                          }}
                        >
                          📊 RM Total
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            color: "#3b82f6",
                            fontSize: "0.95rem",
                          }}
                        />
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            color: "#3b82f6",
                            textAlign: "center",
                            fontSize: "0.95rem",
                          }}
                        >
                          {calculateRmTotal(rmName)}
                        </TableCell>

                        {/* RM Date Totals */}
                        {dateColumns.map((date) => (
                          <TableCell
                            key={date}
                            sx={{
                              textAlign: "center",
                              fontWeight: "bold",
                              color: "#3b82f6",
                              backgroundColor: "rgba(59, 130, 246, 0.15)",
                              fontSize: "0.95rem",
                            }}
                          >
                            {calculateRmDateTotal(rmName, date)}
                          </TableCell>
                        ))}
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Footer Stats with Interactive Cards */}
      {data.length > 0 && (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background:
                  "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                borderRadius: 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 24px rgba(59, 130, 246, 0.3)",
                  border: "1px solid rgba(59, 130, 246, 0.5)",
                },
              }}
            >
              <CardContent sx={{ textAlign: "center" }}>
                <AssignmentIcon
                  sx={{ fontSize: "2rem", color: "#3b82f6", mb: 1 }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                >
                  Total Records
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: "#3b82f6", fontWeight: "bold", mt: 1 }}
                >
                  {data.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background:
                  "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(16, 185, 129, 0.3)",
                borderRadius: 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 24px rgba(16, 185, 129, 0.3)",
                  border: "1px solid rgba(16, 185, 129, 0.5)",
                },
              }}
            >
              <CardContent sx={{ textAlign: "center" }}>
                <TrendingUpIcon
                  sx={{ fontSize: "2rem", color: "#10b981", mb: 1 }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                >
                  Days Tracked
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: "#10b981", fontWeight: "bold", mt: 1 }}
                >
                  {dateColumns.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background:
                  "linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(168, 85, 247, 0.3)",
                borderRadius: 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 24px rgba(168, 85, 247, 0.3)",
                  border: "1px solid rgba(168, 85, 247, 0.5)",
                },
              }}
            >
              <CardContent sx={{ textAlign: "center" }}>
                <GroupIcon sx={{ fontSize: "2rem", color: "#a855f7", mb: 1 }} />
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                >
                  Region Managers
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: "#a855f7", fontWeight: "bold", mt: 1 }}
                >
                  {Object.keys(groupedData).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background:
                  "linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(245, 158, 11, 0.3)",
                borderRadius: 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 24px rgba(245, 158, 11, 0.3)",
                  border: "1px solid rgba(245, 158, 11, 0.5)",
                },
              }}
            >
              <CardContent sx={{ textAlign: "center" }}>
                <Badge
                  badgeContent={stats.totalMr}
                  color="warning"
                  sx={{ fontSize: "2rem", mb: 1 }}
                >
                  <GroupIcon sx={{ fontSize: "2rem", color: "#f59e0b" }} />
                </Badge>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                >
                  Medical Reps
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: "#f59e0b", fontWeight: "bold", mt: 1 }}
                >
                  {stats.totalMr}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DaywiseReport;
